import { useMemo, useState, useEffect } from 'react';
import { useCountry } from './useCountry';
import { getLanguageFromCountry } from '@/utils/translations';

/**
 * Enhanced translation system with:
 * - Type-safe translation keys
 * - Automatic missing key detection
 * - Intelligent fallback (country lang → en → ro → key)
 * - Development warnings for missing translations
 */

export type TranslationNamespace = 
  | 'common'
  | 'navigation'
  | 'cities-hub'
  | 'filters'
  | 'pricing'
  | 'faq'
  | 'dealer'
  | 'dealer-meta-ads'
  | 'forms'
  | 'errors'
  | 'vehicles'
  | 'trucks'
  | 'motorcycles'
  | 'equipment'
  | 'boats'
  | 'demo'
  | 'testimonials'
  | 'assistant'
  | 'mylistings'
  | 'createlisting'
  | 'seo'
  | 'badges'
  | 'hero'
  | 'social-proof'
  | 'why-choose'
  | 'imageupload'
  | 'gdpr';

// Translation loader - dynamically imports JSON files per namespace
const translationCache = new Map<string, Record<string, any>>();
// Lazy translation modules: each path maps to a function that returns a Promise of the module.
// Using { eager: false } keeps translations out of the initial bundle (~247KB savings on first load).
const translationModules = import.meta.glob('/src/data/translations/*/*.json') as Record<string, () => Promise<any>>;

// RO is the primary language and MUST be available synchronously so SSR emits
// real strings instead of raw keys (e.g. "sign_in" in the header).
const roModules = import.meta.glob('/src/data/translations/ro/*.json', { eager: true }) as Record<string, any>;
Object.entries(roModules).forEach(([path, mod]) => {
  const ns = path.split('/').pop()!.replace('.json', '');
  translationCache.set(`${ns}_ro`, (mod?.default ?? mod ?? {}) as Record<string, any>);
});

// Meniul principal și textele comune apar pe fiecare pagină, inclusiv în HTML-ul server-side.
// Îl încărcăm sincron în toate limbile, altfel paginile /de și /uk sunt livrate
// cu etichete în română până termină încărcarea asincronă.
const navModules = import.meta.glob('/src/data/translations/*/{navigation,common,cities-hub}.json', { eager: true }) as Record<string, any>;
Object.entries(navModules).forEach(([path, mod]) => {
  const parts = path.split('/');
  const lang = parts[parts.length - 2];
  const ns = parts[parts.length - 1].replace('.json', '');
  translationCache.set(`${ns}_${lang}`, (mod?.default ?? mod ?? {}) as Record<string, any>);
});


const getSync = (namespace: string, lang: string): Record<string, any> =>
  translationCache.get(`${namespace}_${lang}`) ??
  translationCache.get(`${namespace}_ro`) ??
  {};


// Normalize language codes to match folder structure
const normalizeLanguageCode = (lang: string): string => {
  // Map language-region codes to folder names
  const languageMap: Record<string, string> = {
    'en-GB': 'en-GB', // UK-specific translations
    'en-US': 'en',
    'de-DE': 'de',
    'fr-FR': 'fr',
    'ro-RO': 'ro'
  };
  
  return languageMap[lang] || lang.split('-')[0] || 'ro';
};

const loadTranslation = async (namespace: TranslationNamespace, lang: string): Promise<Record<string, any>> => {
  const normalizedLang = normalizeLanguageCode(lang);
  const cacheKey = `${namespace}_${normalizedLang}`;
  
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }

  // Dynamically import the JSON file only when this namespace+language is requested.
  const path = `/src/data/translations/${normalizedLang}/${namespace}.json`;
  const loader = translationModules[path];

  if (loader) {
    try {
      const module = await loader();
      const data = (module && module.default) ? module.default : {};
      translationCache.set(cacheKey, data);
      return data as Record<string, any>;
    } catch (err) {
      console.warn(`Failed to load translation: ${namespace}.json for language: ${normalizedLang}`, err);
      return {};
    }
  } else {
    console.warn(`Missing translation file: ${namespace}.json for language: ${normalizedLang}`);
    return {};
  }
};

export const useTranslationV2 = (namespace: TranslationNamespace = 'common') => {
  const { country } = useCountry();
  const language = useMemo(() => getLanguageFromCountry(country), [country]);
  
  const [translations, setTranslations] = useState<Record<string, any>>(() =>
    getSync(namespace, normalizeLanguageCode(language)),
  );
  const [isLoading, setIsLoading] = useState(false);


  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      let data = await loadTranslation(namespace, language);

      // Fallbacks: try 'en' then 'ro' if current lang missing
      if (!data || Object.keys(data).length === 0) {
        const enData = await loadTranslation(namespace, 'en');
        if (enData && Object.keys(enData).length > 0) {
          console.info(`[i18n] Fallback to EN for namespace "${namespace}" (lang: ${language})`);
          data = enData;
        } else {
          const roData = await loadTranslation(namespace, 'ro');
          if (roData && Object.keys(roData).length > 0) {
            console.info(`[i18n] Fallback to RO for namespace "${namespace}" (lang: ${language})`);
            data = roData;
          }
        }
      }

      setTranslations(data);
      setIsLoading(false);
    };
    
    loadAll();
  }, [namespace, language]);
  
  const t = useMemo(() => {
    const getByPath = (obj: any, path: string) => {
      try {
        return path.split('.').reduce((acc: any, part: string) => (acc != null ? acc[part] : undefined), obj);
      } catch {
        return undefined;
      }
    };

    return (key: string, fallback?: string): string => {
      // 1. Try direct key
      const direct = translations[key];
      if (typeof direct === 'string') {
        return direct;
      }

      // 2. Try nested path (dot notation)
      const nested = getByPath(translations, key);
      if (typeof nested === 'string') {
        return nested;
      }

      // 3. Synchronous RO fallback — guarantees the SSR string and the first
      // client render agree even if the async namespace load returns nothing.
      const roFallback = getByPath(translationCache.get(`${namespace}_ro`) ?? {}, key);
      if (typeof roFallback === 'string') {
        return roFallback;
      }

      // 4. Development warning — only once the namespace is actually loaded,
      // otherwise every first render logs false positives while the JSON loads.
      if (process.env.NODE_ENV === 'development' && !isLoading && Object.keys(translations).length > 0) {
        console.warn(`Missing translation: "${key}" in namespace "${namespace}" for language "${language}"`);
      }

      
      // 5. Return fallback or key
      return fallback || key;

    };
  }, [translations, namespace, language, isLoading]);
  
  return { t, language, isLoading };
};
